import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import Web3 from 'web3';
import Navbar from './components/Navbar';
import LandingPage from './components/LandingPage';
import PricingPage from './components/PricingPage';
import MarketplacePage from './components/MarketplacePage';
import SellPage from './components/SellPage';
import ListingDetailsPage from './components/ListingDetailsPage';
import HistoryPage from './components/HistoryPage';
import FaucetPage from './components/FaucetPage';
import Popup from './components/Popup';
import WalletModal from './components/WalletModal';
import './App.css';

// Import your ABIs
import { contractABI, usdtContractABI } from './contractABIs';

function App() {
  const [account, setAccount] = useState(null);
  const [web3, setWeb3] = useState(null);
  const [hasSubscribed, setHasSubscribed] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');

  const contractAddress = '0xBd14C5d002cA7E61B2c8f15c8F6C2BEe0E90e0d8';
  const usdtContractAddress = '0x0aa347912773a1f7096cAb7Ab6Bb8043B0B7B5C9';

  useEffect(() => {
    const initWeb3 = async () => {
      if (window.ethereum) {
        const web3Instance = new Web3(window.ethereum);
        setWeb3(web3Instance);

        window.ethereum.on('accountsChanged', handleAccountsChanged);

        try {
          const accounts = await web3Instance.eth.getAccounts();
          if (accounts.length > 0) {
            setAccount(accounts[0]);
            checkSubscriptionStatus(accounts[0], web3Instance);
          }
        } catch (error) {
          console.error("Failed to get accounts", error);
        }
      }
    };

    initWeb3();

    return () => {
      if (window.ethereum && window.ethereum.removeListener) {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      }
    };
  }, []);

  const handleAccountsChanged = async (accounts) => {
    if (accounts.length === 0) {
      // User disconnected their wallet
      setAccount(null);
      setHasSubscribed(false);
    } else {
      const newAccount = accounts[0];
      if (newAccount !== account) {
        setAccount(newAccount);
        if (web3) {
          await checkSubscriptionStatus(newAccount, web3);
        }
      }
    }
  };

  const openWalletModal = () => setShowWalletModal(true);
  const closeWalletModal = () => setShowWalletModal(false);

  const connectWallet = async () => {
    if (web3) {
      try {
        // First, request permission to access accounts
        await window.ethereum.request({
          method: 'wallet_requestPermissions',
          params: [{ eth_accounts: {} }]
        });

        // Then, request the accounts
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });

        if (accounts.length > 0) {
          setAccount(accounts[0]);
          checkSubscriptionStatus(accounts[0], web3);
        }
        closeWalletModal();
      } catch (error) {
        console.error("Failed to connect to MetaMask:", error);
        alert("Failed to connect to MetaMask. Please try again.");
      }
    } else {
      alert("Web3 not initialized. Please refresh the page and try again.");
    }
  };

  const disconnectWallet = () => {
    setAccount(null);
    setHasSubscribed(false);
  };

  const checkSubscriptionStatus = async (userAccount, web3Instance) => {
    if (userAccount) {
      const subscriptionContract = new web3Instance.eth.Contract(contractABI, contractAddress);
      try {
        const isSubscribed = await subscriptionContract.methods.isSubscribed(userAccount).call();
        setHasSubscribed(isSubscribed);
      } catch (error) {
        console.error('Error checking subscription status:', error);
      }
    }
  };

  const onPayNow = async () => {
    if (!account) {
      setPopupMessage("Please connect your wallet first!");
      setShowPopup(true);
      return;
    }

    const usdtContract = new web3.eth.Contract(usdtContractABI, usdtContractAddress);
    const subscriptionContract = new web3.eth.Contract(contractABI, contractAddress);
    const subscriptionAmount = web3.utils.toWei('10', 'ether');

    try {
      await usdtContract.methods.approve(contractAddress, subscriptionAmount).send({ from: account });
      await subscriptionContract.methods.subscribe().send({ from: account });
      setPopupMessage('Payment successful! You now have access.');
      setShowPopup(true);
      setHasSubscribed(true);
    } catch (error) {
      console.error('Payment failed:', error);
      setPopupMessage('Payment failed. Please try again.');
      setShowPopup(true);
    }
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  return (
    <Router>
      <div className="App">
        <Navbar
          account={account}
          openWalletModal={openWalletModal}
          disconnectWallet={disconnectWallet}
        />
        <Routes>
          <Route path="/" element={<LandingPage openWalletModal={openWalletModal} />} />
          <Route path="/pricing" element={<PricingPage onPayNow={onPayNow} hasSubscribed={hasSubscribed} />} />
          <Route path="/marketplace" element={<MarketplacePage hasSubscribed={hasSubscribed} />} />
          <Route
            path="/sell"
            element={hasSubscribed ? <SellPage /> : <Navigate to="/pricing" replace />}
          />
          <Route
            path="/listing/:id"
            element={hasSubscribed ? <ListingDetailsPage /> : <Navigate to="/pricing" replace />}
          />
          <Route path="/history" element={<HistoryPage />} />
          <Route path="/faucet" element={<FaucetPage account={account} />} /> {/* Add this new route */}
        </Routes>
        <WalletModal
          show={showWalletModal}
          onClose={closeWalletModal}
          connectWallet={connectWallet}
        />
        {showPopup && <Popup message={popupMessage} onClose={handleClosePopup} />}
      </div>
    </Router>
  );
}

export default App;